import React from 'react';
import { action, computed, observable, toJS } from 'mobx';

import { ContractTypeModel, PersonContractValidationResult, PersonContractViewModel, PersonFunctionValidationResult, PersonFunctionViewModel } from '@app/Models';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import ApiService from '@app/Services/ApiService';
import { ApiUrls, Permissions } from '@app/AppConstants';
import { modalService } from '@app/Components/Modal';
import { NotificationHandler } from '@app/Components/ToastNotification';
import { securityService } from '@app/Services/SecurityService';

export interface IEntryExitBlockStore {
    personContracts: PersonContractViewModel[];
    personFunctions: PersonFunctionViewModel[];
    personId: string;

    findSelectedContract(contractId: string): PersonContractViewModel | undefined;
}

export class EntryExitBlockStore implements IEntryExitBlockStore {
    @observable personContracts: PersonContractViewModel[];
    @observable personFunctions: PersonFunctionViewModel[];
    @observable activeRow: PersonContractViewModel | null = null;
    @observable personId: string;
    public prefix: string = 'tooltip-item-';
    public personFunctionLoader: PromiseCompletion = new PromiseCompletion();
    public personContractLoader: PromiseCompletion = new PromiseCompletion();

    constructor(personId: string) {
        this.personId = personId;
        void this.loadPersonContract(personId);
        void this.loadPersonFunction(personId);
    }

    @computed
    get activeRowId(): string {
        return this.prefix + (this.activeRow && this.activeRow.id || '');
    }

    @action
    async loadPersonFunction(selectedPersonId: string) {
        const params = { personId: selectedPersonId };
        const { data: functions } = await ApiService.getTypedData<PersonFunctionViewModel[]>(ApiUrls.PersonFunctionUrl, params, {completion: this.personFunctionLoader});
        this.personFunctions = functions;
    }

    @action
    async loadPersonContract(selectedPersonId: string) {
        const params = { personId: selectedPersonId };
        const { data: contracts } = await ApiService.getTypedData<PersonContractViewModel[]>(ApiUrls.PersonContractUrl, params, { completion: this.personContractLoader });
        this.personContracts = contracts;
    }

    @computed
    get groupedPersonContracts(): PersonContractViewModel[] {
        const groupMap = this.personContracts.reduce((acc, cur) => {
            const array = acc[cur.externalId] ?? (acc[cur.externalId] = []);
            array.push(cur);
            return acc;
        }, {} as {[key: string]: PersonContractViewModel[]});
        // Provide main contracts with full date range. Expected desc order by DateFrom.
        return Object.values(groupMap).map(contracts => {
            const main = contracts[contracts.length - 1];
            const last = contracts[0];
            let isFinalizedFully: boolean | null = null;
            if (contracts.every(x => x.isFinalized)){
                isFinalizedFully = true;
            }
            if (contracts.every(x => !x.isFinalized)){
                isFinalizedFully = false;
            }
            return {
                ...main,
                dateTo: last.dateTo,
                isFinalized: isFinalizedFully
            } as PersonContractViewModel;
        });
    }

    @action.bound
    async updateContract(params: PersonContractViewModel, ignoreWarnings?: boolean, isContract?: boolean): Promise<boolean> {
        const model = Object.assign({}, params, { ignoreWarnings: ignoreWarnings });

        const { data: result } = await ApiService.putTypedData<PersonContractValidationResult>(ApiUrls.PersonContractUrl, model, { completion: this.personContractLoader });

        const updateModel = () => {
            const contracts = result.value as PersonContractViewModel[];
            if (contracts && contracts.length > 0) {
                this.personContracts = this.personContracts.map(item => contracts.find(updated => updated.id === item.id) || item);
            }
        };

        if (result.hasErrors && result.errors) {
            await modalService.showInformation(result.errors.join('\r\n '));
            updateModel();
            return false;
        }

        if (result.hasWarnings && result.warnings && !ignoreWarnings) {
            if (!securityService.hasPermission(Permissions.EditContractIsFinalizedWithNotMatchedPercentage, true)){
                await modalService.showInformation(result.warnings.join('\r\n '));
                updateModel();
                return false;
            }

            if (await modalService.showConfirmation(result.warnings.join('\r\n '), {
                color: 'warning',
                confirmButtonTitle: 'Ignore and save anyway'
            })){
                return await this.updateContract(params, true);
            }

            updateModel();
            return false;
        }

        updateModel();
        const entityDisplayName = !isContract ? 'Person Contract Model' : 'Person Contract';
        NotificationHandler.showSuccess(`${entityDisplayName} was updated`, 'Success message');
        return true;
    }

    @action.bound
    async updateFunction(params: PersonFunctionViewModel) {
        const { data: result } = await ApiService.putTypedData<PersonFunctionValidationResult>(ApiUrls.PersonFunctionUrl, params);

        if (result.hasErrors && result.errors) {
            await modalService.showInformation(result.errors.join('\r\n '));
        } else {
            NotificationHandler.showSuccess('Person Function was updated', 'Success message');
        }

        const updatedFunction = result.value as PersonFunctionViewModel;
        if (updatedFunction) {
            this.personFunctions = this.personFunctions.map((item: PersonFunctionViewModel) => item.id === updatedFunction.id ? updatedFunction : item);
        }
    }

    @computed
    get needToShowReason(): boolean {
        if (!this.activeRow)
            return false;

        return this.activeRow.typeModel === ContractTypeModel.TZV_FixDay && !!this.activeRow.partTimeFixDayReason;
    }

    renderReason() {
        return this.activeRow && this.activeRow.partTimeFixDayReason ? <span>Reason: {this.activeRow.partTimeFixDayReason}</span> : null;
    }

    findSelectedContract(contractId: string): PersonContractViewModel | undefined {
        const contractModel = this.personContracts.find(x => x.id === contractId);
        if (!contractModel) {
            return;
        }
        const contracts = this.personContracts.filter(x => x.externalId === contractModel.externalId);
        const end = toJS(contracts[0]);
        const start = toJS(contracts[contracts.length - 1]);
        return {
            ...start,
            dateTo: end.dateTo,
        };
    }
}
